import React, { useEffect, useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { Text, useTheme, Card, Icon } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../types/navigationParams";
import { FieldComponentProps } from "./fieldRegistry";
import {
  collection,
  db,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "../../firebase";
import { useTranslation } from "react-i18next";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Message {
  _id: string;
  text: string;
  createdAt: Date;
  user: {
    _id: string;
    name: string;
  };
}

export const ChatField: React.FC<
  Omit<FieldComponentProps, "onChange" | "formData" | "collection"> & {
    title?: string;
  }
> = ({ id, title }) => {
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const messagesRef = collection(db, "messages");
    const q = query(
      messagesRef,
      where("chatId", "==", id),
      orderBy("createdAt", "desc"),
      limit(3),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          _id: doc.id,
          text: data.text,
          createdAt: data.createdAt.toDate(),
          user: data.user,
        };
      });
      setMessages(fetchedMessages.reverse());
      setLoading(false);
    });

    return () => unsubscribe();
  }, [id]);

  const handlePress = () => {
    if (id) {
      navigation.navigate("ChatScreen", { chatId: id, title });
    }
  };

  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={handlePress}
      disabled={!id}
    >
      <Card
        style={[styles.card, { backgroundColor: theme.colors.surfaceVariant }]}
      >
        <Card.Content>
          {loading ? (
            <Text style={styles.loadingText}>
              {t("chatField.loadingMessages")}
            </Text>
          ) : messages.length === 0 ? (
            <Text style={styles.emptyText}>{t("chatField.noMessages")}</Text>
          ) : (
            <View style={styles.messagesContainer}>
              {messages.map((message) => (
                <View key={message._id} style={styles.messageRow}>
                  <Text style={styles.userName}>{message.user.name}:</Text>
                  <Text style={styles.messageText} numberOfLines={1}>
                    {message.text}
                  </Text>
                  <Text style={styles.timeText}>
                    {formatTime(message.createdAt)}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  card: {
    borderRadius: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  messagesContainer: {
    gap: 8,
  },
  messageRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  userName: {
    fontWeight: "600",
    fontSize: 13,
  },
  messageText: {
    flex: 1,
    fontSize: 13,
  },
  timeText: {
    fontSize: 11,
    opacity: 0.6,
  },
  loadingText: {
    fontSize: 13,
    opacity: 0.6,
    fontStyle: "italic",
  },
  emptyText: {
    fontSize: 13,
    opacity: 0.6,
    fontStyle: "italic",
  },
});
